import React, { useCallback, useEffect, useMemo, useState } from "react";
import InstructorAhaIdField from "../components/certificates/InstructorAhaIdField";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import SchoolIcon from "@mui/icons-material/School";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { useTheme, alpha } from "@mui/material/styles";
import { useTenant } from "../contexts/TenantContext";
import { useActiveTenant } from "../hooks/useActiveTenant";
import { useConfigList } from "../hooks/useConfigList";
import NoTenantSelected from "../components/NoTenantSelected";
import { getCategoryColor } from "../constants/categoryColors";
import {
  fetchTenantUsersByRole,
  removeTenantRole,
  fetchTenantRoleInvitations,
  sendInstructorEmailInvitation,
  fetchInstructorEmailInvitations,
  fetchInstructorEmailInvitationHistory,
  revokeInstructorEmailInvitation,
  resendInstructorEmailInvitation,
  fetchClasses,
  updateClass,
  fetchInstructorQualifications,
  setInstructorQualifications,
} from "../api/apiClient";

// LEAD_INSTRUCTOR is a fixed, reserved role (backend TeachingRole.LEAD_INSTRUCTOR) - always offered,
// never sourced from the tenant's config list. Everything else is tenant-configurable free text.
const DEFAULT_OTHER_TEACHING_ROLES = ["ASSISTANT_INSTRUCTOR", "TA"];


// ── Design tokens ─────────────────────────────────────────────────────────────
const DS = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)", line3: "var(--color-surface-2)",
  surface: "#FFFFFF", surface2: "var(--color-surface-press-light)", surface3: "var(--color-surface-press-light)",
  live: "#C2410C", live50: "rgba(194,65,12,.10)", live100: "rgba(194,65,12,.22)",
  danger: "#B42318", danger50: "rgba(180,35,24,.08)", danger100: "rgba(180,35,24,.18)",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)", blue100: "rgba(37,99,235,.18)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
  shadowMd: "0 8px 24px -10px rgba(20,24,40,.15)",
};

const AVATAR_COLORS = ["var(--color-primary)", "var(--color-primary)", "#15803D", "#B5651D", "var(--color-ink-3)", "#C2410C", "#0E5C6B"];

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  return diff;
}

// ── Seat Bar ──────────────────────────────────────────────────────────────────
function SeatBar({ available, total }) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  const tAccent = theme.palette.warning.main;
  const filled = Math.max(0, total - (available || 0));
  const pct = total > 0 ? (filled / total) * 100 : 0;

  let barColor = tSecondary;
  if (available === 0) {
    barColor = DS.danger;
  } else if (available <= 3 || (total > 0 && available / total <= 0.25)) {
    barColor = tAccent;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0, minWidth: 120 }}>
      <Box sx={{ fontSize: "11.5px", fontWeight: 700, color: available === 0 ? DS.danger : available <= 3 ? tAccent : tSecondary, whiteSpace: "nowrap" }}>
        {available} seat{available !== 1 ? "s" : ""} left
      </Box>
      <Box sx={{ width: "60px", height: "5px", bgcolor: DS.line2, borderRadius: "999px", overflow: "hidden" }}>
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            bgcolor: barColor,
            borderRadius: "999px",
            transition: "width .25s ease",
          }}
        />
      </Box>
    </Box>
  );
}

// ── Invite Dialog ─────────────────────────────────────────────────────────────
function InviteDialog({ open, tenantId, onSuccess, onClose }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary700 = theme.palette.primary.dark;

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setFullName("");
      setError(null);
    }
  }, [open]);

  const handleSend = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      await sendInstructorEmailInvitation(trimmed, tenantId);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to send invitation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,.25)" } }}>
      <Box sx={{
        display: "flex", alignItems: "flex-start", gap: "14px",
        p: "22px 24px 18px", borderBottom: `1px solid ${DS.line2}`,
      }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: "8px",
          bgcolor: tPrimary, color: "#fff",
          display: "grid", placeItems: "center", flexShrink: 0,
        }}>
          <PersonAddIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, color: DS.ink, fontFamily: "var(--font-display)" }}>
            Invite Instructor
          </Typography>
          <Typography sx={{ fontSize: "13px", color: DS.ink, opacity: 0.6, mt: "3px" }}>
            Send an invitation email to onboard a new instructor
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{
          width: 32, height: 32, borderRadius: "6px",
          border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, opacity: 0.6,
          "&:hover": { bgcolor: DS.danger50, color: DS.danger, opacity: 1 },
        }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {error && <Alert severity="error" sx={{ borderRadius: "10px" }} onClose={() => setError(null)}>{error}</Alert>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          <Typography sx={{ fontSize: "11.5px", fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: DS.ink, opacity: 0.55 }}>
            Full Name
          </Typography>
          <TextField
            placeholder="e.g. Jordan Williams"
            fullWidth
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 44, borderRadius: "8px", bgcolor: DS.surface2,
                "& fieldset": { borderColor: DS.line },
                "&:hover fieldset": { borderColor: DS.ink4 },
                "&.Mui-focused fieldset": { borderColor: tPrimary },
                "&.Mui-focused": { bgcolor: DS.surface },
              },
              "& .MuiInputBase-input": { fontSize: "14px" },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          <Typography sx={{ fontSize: "11.5px", fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: DS.ink, opacity: 0.55 }}>
            Email Address <Box component="span" sx={{ color: DS.danger }}>*</Box>
          </Typography>
          <TextField
            type="email"
            placeholder="instructor@organization.gov"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && email.trim() && handleSend()}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 44, borderRadius: "8px", bgcolor: DS.surface2,
                "& fieldset": { borderColor: DS.line },
                "&:hover fieldset": { borderColor: DS.ink4 },
                "&.Mui-focused fieldset": { borderColor: tPrimary },
                "&.Mui-focused": { bgcolor: DS.surface },
              },
              "& .MuiInputBase-input": { fontSize: "14px" },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: "24px", pb: "18px", pt: "14px",
        borderTop: `1px solid ${DS.line2}`, bgcolor: DS.surface2,
        display: "flex", justifyContent: "flex-end", gap: "9px",
      }}>
        <Button onClick={onClose} sx={{
          height: 38, px: "16px", borderRadius: "8px",
          border: `1px solid ${DS.line}`, bgcolor: DS.surface,
          color: DS.ink, fontSize: "13px", fontWeight: 500, textTransform: "none",
          "&:hover": { bgcolor: DS.surface3 },
        }}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          disabled={loading || !email.trim()}
          startIcon={<SendIcon sx={{ fontSize: 13 }} />}
          sx={{
            height: 38, px: "16px", borderRadius: "8px", border: 0,
            bgcolor: tPrimary, color: "#fff", fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".2px",
            "&:hover": { bgcolor: tPrimary700 },
          }}
        >
          {loading ? "Sending…" : "Send Invitation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Instructor Row ───────────────────────────────────────────────────────────
function InstructorRow({
  inst, selectedTenantId, isExpanded, toggleExpand, instClasses, canEditAhaId,
  teachingRoles, onEditRoles, onAssign, onRemove, teachingRoleOptions, tPrimary, tPrimary10, tPrimary50, tSecondary, tSecondary50,
}) {
  const name = inst.fullName || inst.username;
  const roles = Array.isArray(inst.tenantRoles)
    ? inst.tenantRoles
    : (inst.tenantRoles?.[selectedTenantId] || []);
  const isActive = roles.includes("INSTRUCTOR");
  const categories = [...new Set(instClasses.map((c) => c.category).filter(Boolean))];

  return (
    <Box sx={{ borderBottom: `1px solid ${DS.line2}`, "&:last-child": { borderBottom: 0 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px", p: "14px 20px", transition: "background .12s", "&:hover": { bgcolor: DS.surface2 } }}>
        <Avatar sx={{ width: 40, height: 40, fontSize: "14px", fontWeight: 600, letterSpacing: "-.01em", bgcolor: getAvatarColor(name), color: "#fff", flexShrink: 0, fontFamily: "var(--font-display)" }}>
          {getInitials(name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", mb: "3px" }}>
            <Typography sx={{ fontSize: "14.5px", fontWeight: 600, color: DS.ink }}>{name}</Typography>
            <Box sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", px: "8px", py: "2px", borderRadius: "999px", bgcolor: isActive ? tSecondary50 : DS.surface3, color: isActive ? tSecondary : DS.ink, opacity: isActive ? 1 : 0.6, border: isActive ? "none" : `1px solid ${DS.line}` }}>
              {isActive ? "Active" : "Unassigned"}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: DS.ink, opacity: 0.55, fontWeight: 500 }}>
              <MailOutlineIcon sx={{ fontSize: 12, opacity: 0.7 }} />{inst.email || "—"}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: DS.ink, opacity: 0.55, fontWeight: 500 }}>
              <SchoolIcon sx={{ fontSize: 12, opacity: 0.7 }} />{instClasses.length} active course{instClasses.length !== 1 ? "s" : ""}
            </Box>
            <Box sx={{ fontSize: "12px", color: DS.ink, opacity: teachingRoles.length ? 0.55 : 0.8, fontWeight: 500 }}>
              {teachingRoles.length
                ? `Teaching: ${teachingRoleOptions.filter((r) => teachingRoles.includes(r.value)).map((r) => r.label).join(", ")}`
                : "No teaching roles — can't claim open slots"}
            </Box>
          </Box>
        </Box>
        {categories.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {categories.map((cat) => (
              <Box key={cat} sx={{ fontSize: "11.5px", fontWeight: 600, color: getCategoryColor(cat), px: "10px", py: "3px", borderRadius: "999px", background: `color-mix(in srgb, ${getCategoryColor(cat)} 15%, transparent)` }}>
                {cat}
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
          <Tooltip title={isExpanded ? "Hide courses" : "Show courses"}>
            <IconButton size="small" onClick={() => toggleExpand(inst.id)} sx={{ width: 34, height: 34, borderRadius: "8px", border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, opacity: 0.7, transition: "transform .2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", "&:hover": { bgcolor: DS.surface2, opacity: 1 } }}>
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Button size="small" onClick={onEditRoles} sx={{ height: 34, px: "13px", borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "12.5px", whiteSpace: "nowrap", border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, opacity: 0.8, "&:hover": { bgcolor: tPrimary10, borderColor: tPrimary50, color: tPrimary, opacity: 1 } }}>
            Teaching Roles
          </Button>
          <Button size="small" onClick={onAssign} sx={{ height: 34, px: "13px", borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "12.5px", whiteSpace: "nowrap", border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, opacity: 0.8, "&:hover": { bgcolor: tPrimary10, borderColor: tPrimary50, color: tPrimary, opacity: 1 } }}>
            <AddIcon sx={{ fontSize: 12, mr: "6px" }} />Assign Course
          </Button>
          <Tooltip title="Remove instructor role">
            <IconButton size="small" onClick={onRemove} sx={{ width: 34, height: 34, borderRadius: "8px", border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, opacity: 0.7, "&:hover": { bgcolor: DS.danger50, color: DS.danger, borderColor: DS.danger100, opacity: 1 } }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        {isExpanded && <InstructorAhaIdField userId={inst.id} canEdit={canEditAhaId} />}
        <Box sx={{ px: "20px", pb: "14px", pt: "2px", bgcolor: DS.surface2, borderTop: `1px solid ${DS.line2}` }}>
          {instClasses.length === 0 ? (
            <Typography sx={{ fontSize: "12.5px", color: DS.ink3, py: "10px" }}>No courses assigned yet.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", pt: "10px" }}>
              {instClasses.map((cls) => {
                const isLead = Number(cls.leadInstructorId) === Number(inst.id);
                return (
                  <Box key={cls.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", px: "12px", py: "8px", borderRadius: "10px", bgcolor: DS.surface, border: `1px solid ${DS.line}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                      <SchoolIcon sx={{ fontSize: 14, color: DS.ink4, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: DS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cls.title}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                      <Box sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", px: "7px", py: "2px", borderRadius: "999px", bgcolor: isLead ? tPrimary10 : DS.surface3, color: isLead ? tPrimary : DS.ink3, border: `1px solid ${isLead ? tPrimary50 : DS.line}` }}>
                        {isLead ? "Lead" : "Supporting"}
                      </Box>
                      {cls.availableSeats != null && (
                        <SeatBar available={cls.availableSeats} total={cls.capacity || cls.maxStudents || cls.availableSeats} />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function InstructorManagementPage() {
  const { user, tenantConfig } = useTenant();
  const { tenantId: selectedTenantId, isGlobalAdmin } = useActiveTenant();
  const otherTeachingRoles = useConfigList("TEACHING_ROLE", DEFAULT_OTHER_TEACHING_ROLES);
  const TEACHING_ROLES = [
    { value: "LEAD_INSTRUCTOR", label: "Lead Instructor" },
    ...otherTeachingRoles.map((r) => ({ value: r, label: r })),
  ];
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary700 = theme.palette.primary.dark;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tPrimary50 = alpha(tPrimary, 0.10);
  const tSecondary = theme.palette.secondary.main;
  const tSecondary50 = alpha(tSecondary, 0.10);
  const tAccent = theme.palette.warning.main;
  const tAccent50 = alpha(tAccent, 0.10);
  const tAccent100 = alpha(tAccent, 0.22);

  const [instructors, setInstructors] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [emailInvitations, setEmailInvitations] = useState([]);
  const [inviteHistory, setInviteHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [classes, setClasses] = useState([]);
  const [expandedInstructors, setExpandedInstructors] = useState(new Set());
  const [assignDialog, setAssignDialog] = useState(null);
  const [assignCourseId, setAssignCourseId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [qualifications, setQualifications] = useState({});
  const [rolesDialog, setRolesDialog] = useState(null); // { instructor, selected: string[] }
  const [rolesSaving, setRolesSaving] = useState(false);

  const canManage = user?.roles?.some((r) => ["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN"].includes(r));

  const load = useCallback(async () => {
    if (!selectedTenantId || !canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        fetchTenantUsersByRole(selectedTenantId, "INSTRUCTOR"),
        fetchTenantRoleInvitations(selectedTenantId, "INSTRUCTOR"),
        fetchInstructorEmailInvitations(selectedTenantId),
        fetchInstructorEmailInvitationHistory(selectedTenantId),
        fetchClasses(selectedTenantId, ""),
        fetchInstructorQualifications(selectedTenantId),
      ]);
      const [usersRes, invitesRes, emailInvitesRes, historyRes, classesRes, qualsRes] = results;
      setQualifications(qualsRes.status === "fulfilled" && qualsRes.value ? qualsRes.value : {});

      if (usersRes.status === "fulfilled") {
        const data = usersRes.value;
        setInstructors(Array.isArray(data) ? data : []);
      } else {
        setInstructors([]);
        console.error("Failed to load instructors:", usersRes.reason);
      }

      if (invitesRes.status === "fulfilled") {
        const data = invitesRes.value;
        setPendingInvitations(Array.isArray(data) ? data : []);
      } else {
        setPendingInvitations([]);
      }

      if (emailInvitesRes.status === "fulfilled") {
        const data = emailInvitesRes.value;
        setEmailInvitations(Array.isArray(data) ? data : []);
      } else {
        setEmailInvitations([]);
      }

      if (historyRes.status === "fulfilled") {
        setInviteHistory(Array.isArray(historyRes.value) ? historyRes.value : []);
      } else {
        setInviteHistory([]);
      }

      if (classesRes.status === "fulfilled") {
        const data = classesRes.value;
        setClasses(Array.isArray(data) ? data : (data?.content || []));
      } else {
        setClasses([]);
      }

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        setError("Some data could not be loaded. Instructors are shown if available.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load instructor data.");
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, canManage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (invId) => {
    try {
      await revokeInstructorEmailInvitation(invId);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to revoke invitation.");
    }
  };

  const handleResend = async (invId) => {
    try {
      await resendInstructorEmailInvitation(invId);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to resend invitation.");
    }
  };

  const handleRemove = async (inst) => {
    if (!window.confirm(`Remove instructor role from ${inst.fullName || inst.username}?`)) return;
    try {
      await removeTenantRole(inst.id, selectedTenantId, "INSTRUCTOR");
      load();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to remove instructor role.");
    }
  };

  const handleExport = () => {
    const rows = [
      ["Name", "Username", "Email", "Roles"],
      ...instructors.map((i) => [
        i.fullName || i.username,
        i.username,
        i.email || "",
        (i.tenantRoles?.[selectedTenantId] || []).join(", "),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "instructors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleExpand = (instId) => {
    setExpandedInstructors((prev) => {
      const next = new Set(prev);
      if (next.has(instId)) next.delete(instId);
      else next.add(instId);
      return next;
    });
  };

  const getInstructorClasses = (instId) => {
    const id = Number(instId);
    return classes.filter((c) => Number(c.leadInstructorId) === id);
  };

  const handleAssignCourse = async () => {
    if (!assignDialog || !assignCourseId) return;
    setAssignLoading(true);
    try {
      const cls = classes.find((c) => String(c.id) === String(assignCourseId));
      if (!cls) return;
      const instId = Number(assignDialog.instructor.id);
      await updateClass(selectedTenantId, cls.id, { leadInstructorId: instId });
      await load();
      setAssignDialog(null);
      setAssignCourseId("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to assign course.");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSaveRoles = async () => {
    if (!rolesDialog) return;
    setRolesSaving(true);
    try {
      const saved = await setInstructorQualifications(rolesDialog.instructor.id, rolesDialog.selected, selectedTenantId);
      setQualifications((q) => ({ ...q, [rolesDialog.instructor.id]: saved?.teachingRoles || rolesDialog.selected }));
      setRolesDialog(null);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save teaching roles.");
    } finally {
      setRolesSaving(false);
    }
  };

  const allPending = useMemo(() => {
    const roleInvites = pendingInvitations.map((inv) => ({
      id: `role-${inv.id || Math.random()}`,
      type: "role",
      name: inv.invitedUserName || inv.invitedUserUsername || "—",
      email: inv.invitedUserEmail || "—",
      date: inv.createdAt,
      status: "Pending",
    }));
    const emailInvites = emailInvitations.map((inv) => ({
      id: `email-${inv.id}`,
      rawId: inv.id,
      type: "email",
      name: inv.email?.split("@")[0] || "—",
      email: inv.email,
      date: inv.createdAt,
      expires: inv.expiresAt,
      status: "Awaiting signup",
    }));
    return [...roleInvites, ...emailInvites];
  }, [pendingInvitations, emailInvitations]);

  const visibleInstructors = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (statusFilter === "all" && !q) return instructors;
    return instructors.filter((inst) => {
      if (!inst) return false;
      const name = (inst.fullName || inst.username || "").toLowerCase();
      const email = (inst.email || "").toLowerCase();
      const matchQ = !q || name.includes(q) || email.includes(q);
      if (!matchQ) return false;
      if (statusFilter === "all") return true;
      // tenantRoles may be a flat array ["INSTRUCTOR"] or a map { tenantId: [...] }
      const roles = Array.isArray(inst.tenantRoles)
        ? inst.tenantRoles
        : (inst.tenantRoles?.[selectedTenantId] || []);
      if (statusFilter === "active") return roles.includes("INSTRUCTOR");
      if (statusFilter === "unassigned") return roles.length <= 1;
      return true;
    });
  }, [instructors, searchQuery, statusFilter, selectedTenantId]);

  const classLoadItems = useMemo(() => {
    const withCapacity = classes.filter((c) => (c.capacity || c.maxStudents) > 0);
    return (withCapacity.length > 0 ? withCapacity : classes).slice(0, 3);
  }, [classes]);

  if (!canManage) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
        <Alert severity="error">You do not have permission to manage instructors.</Alert>
      </Box>
    );
  }

  if (!loading && !selectedTenantId) {
    if (isGlobalAdmin) {
      return (
        <Box sx={{ maxWidth: 1160, mx: "auto", mt: 4 }}>
          <NoTenantSelected />
        </Box>
      );
    }
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
        <Alert severity="warning">
          Your account has no tenant assigned. Contact an administrator to be assigned to a training center.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ maxWidth: 1160, mx: "auto" }}>

        {/* Page header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "20px", mb: "24px", flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: "30px", fontWeight: 600, letterSpacing: "-.01em", color: DS.ink, lineHeight: 1.15, fontFamily: "var(--font-display)" }}>
              Instructor Management
            </Typography>
            <Typography sx={{ fontSize: "14px", color: DS.ink, opacity: 0.6, mt: "7px" }}>
              {tenantConfig?.name ? `${tenantConfig.name} · ` : ""}Manage instructors, assign courses, and onboard new staff
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexShrink: 0, pt: "2px" }}>
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
              onClick={handleExport}
              sx={{
                height: 38, px: "16px", borderRadius: "8px", textTransform: "none", fontWeight: 500, fontSize: "13px",
                border: `1px solid ${DS.line}`, color: DS.ink, bgcolor: DS.surface,
                "&:hover": { bgcolor: DS.surface2 },
              }}
            >
              Export
            </Button>
            <Button
              startIcon={<PersonAddIcon sx={{ fontSize: 13 }} />}
              onClick={() => setInviteOpen(true)}
              sx={{
                height: 38, px: "16px", borderRadius: "8px", textTransform: "uppercase", fontWeight: 700, fontSize: "13px", letterSpacing: ".2px",
                bgcolor: tPrimary, color: "#fff", border: 0,
                "&:hover": { bgcolor: tPrimary700 },
              }}
            >
              Invite Instructor
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress size={36} sx={{ color: tPrimary }} />
          </Box>
        ) : (
          <>
            {/* Main grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 300px" }, gap: "22px", alignItems: "start" }}>

              {/* Main column */}
              <Box>
                {/* Active Instructors */}
                <Paper elevation={0} sx={{ border: `1px solid ${DS.line}`, borderRadius: "12px", overflow: "hidden", mb: "18px" }}>
                  {/* Section head */}
                  <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    p: "16px 20px 14px", borderBottom: `1px solid ${DS.line2}`,
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
                      <Box sx={{
                        width: 30, height: 30, borderRadius: "6px", flexShrink: 0,
                        display: "grid", placeItems: "center", bgcolor: tPrimary10, color: tPrimary,
                      }}>
                        <PersonIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: "15.5px", fontWeight: 600, color: DS.ink, fontFamily: "var(--font-display)" }}>
                        Active Instructors
                      </Typography>
                      <Box sx={{
                        fontSize: "11px", fontWeight: 600, px: "8px", py: "2px", borderRadius: "999px",
                        bgcolor: DS.surface3, color: DS.ink, opacity: 0.7,
                      }}>
                        {instructors.length}
                      </Box>
                    </Box>
                  </Box>

                  {/* Search bar */}
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: "10px",
                    p: "13px 20px", borderBottom: `1px solid ${DS.line2}`, bgcolor: DS.surface2,
                  }}>
                    <Box sx={{ position: "relative", flex: 1 }}>
                      <SearchIcon sx={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: DS.ink, opacity: 0.4, fontSize: 15, pointerEvents: "none" }} />
                      <InputBase
                        placeholder="Search instructors…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                          width: "100%", height: 38, pl: "36px", pr: "14px",
                          bgcolor: DS.surface, border: `1px solid ${DS.line}`, borderRadius: "6px",
                          fontSize: "13.5px", color: DS.ink,
                          "&.Mui-focused": { borderColor: tPrimary },
                        }}
                      />
                    </Box>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      size="small"
                      sx={{
                        height: 38, minWidth: 130,
                        "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "13px", fontWeight: 600, bgcolor: DS.surface },
                      }}
                    >
                      <MenuItem value="all">All status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="unassigned">Unassigned</MenuItem>
                    </Select>
                  </Box>

                  {/* Instructor list */}
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {visibleInstructors.length === 0 ? (
                      instructors.length > 0 ? (
                        <>
                          <Box sx={{ p: "12px 20px", borderBottom: `1px solid ${DS.line2}`, bgcolor: DS.surface2 }}>
                            <Typography sx={{ fontSize: "13px", color: DS.ink3 }}>
                              {searchQuery ? "No instructors match your search." : "Filtering returned no results — showing all instructors."}
                            </Typography>
                          </Box>
                          {instructors.map((inst) => (
                            <InstructorRow canEditAhaId={canManage}
                              key={inst.id}
                              inst={inst}
                              selectedTenantId={selectedTenantId}
                              isExpanded={expandedInstructors.has(inst.id)}
                              toggleExpand={toggleExpand}
                              instClasses={getInstructorClasses(inst.id)}
                              teachingRoles={qualifications[inst.id] || []}
                              onEditRoles={() => setRolesDialog({ instructor: inst, selected: qualifications[inst.id] || [] })}
                          onAssign={() => { setAssignDialog({ instructor: inst }); setAssignCourseId(""); }}
                              onRemove={() => handleRemove(inst)}
                              teachingRoleOptions={TEACHING_ROLES}
                              tPrimary={tPrimary} tPrimary10={tPrimary10} tPrimary50={tPrimary50}
                              tSecondary={tSecondary} tSecondary50={tSecondary50}
                            />
                          ))}
                        </>
                      ) : (
                        <Box sx={{ p: "24px 20px", textAlign: "center" }}>
                          <Typography sx={{ fontSize: "14px", color: DS.ink3 }}>
                            {searchQuery ? "No instructors match your search." : "No instructors have been assigned yet."}
                          </Typography>
                        </Box>
                      )
                    ) : (
                      visibleInstructors.map((inst) => (
                        <InstructorRow canEditAhaId={canManage}
                          key={inst.id}
                          inst={inst}
                          selectedTenantId={selectedTenantId}
                          isExpanded={expandedInstructors.has(inst.id)}
                          toggleExpand={toggleExpand}
                          instClasses={getInstructorClasses(inst.id)}
                          teachingRoles={qualifications[inst.id] || []}
                          onEditRoles={() => setRolesDialog({ instructor: inst, selected: qualifications[inst.id] || [] })}
                          onAssign={() => { setAssignDialog({ instructor: inst }); setAssignCourseId(""); }}
                          onRemove={() => handleRemove(inst)}
                          teachingRoleOptions={TEACHING_ROLES}
                          tPrimary={tPrimary} tPrimary10={tPrimary10} tPrimary50={tPrimary50}
                          tSecondary={tSecondary} tSecondary50={tSecondary50}
                        />
                      ))
                    )}
                  </Box>
                </Paper>

                {/* Pending Invitations */}
                <Paper elevation={0} sx={{ border: `1px solid ${DS.line}`, borderRadius: "12px", overflow: "hidden", mb: "18px" }}>
                  <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    p: "16px 20px 14px", borderBottom: `1px solid ${DS.line2}`,
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
                      <Box sx={{
                        width: 30, height: 30, borderRadius: "6px", flexShrink: 0,
                        display: "grid", placeItems: "center", bgcolor: tAccent50, color: tAccent,
                      }}>
                        <MailOutlineIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: "15.5px", fontWeight: 600, color: DS.ink, fontFamily: "var(--font-display)" }}>
                        Pending Invitations
                      </Typography>
                      <Box sx={{
                        fontSize: "11px", fontWeight: 600, px: "8px", py: "2px", borderRadius: "999px",
                        bgcolor: tAccent50, color: tAccent,
                      }}>
                        {allPending.length}
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                      onClick={() => setInviteOpen(true)}
                      sx={{
                        height: 32, px: "13px", borderRadius: "8px", textTransform: "none", fontWeight: 500, fontSize: "11.5px",
                        border: `1px solid ${DS.line}`, color: DS.ink, bgcolor: DS.surface,
                        "&:hover": { bgcolor: DS.surface2 },
                      }}
                    >
                      Send New
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {allPending.length === 0 ? (
                      <Box sx={{ p: "28px 20px", textAlign: "center" }}>
                        <Box sx={{
                          width: 44, height: 44, borderRadius: "50%", mx: "auto", mb: "10px",
                          display: "grid", placeItems: "center", bgcolor: DS.surface3,
                          color: DS.ink4, border: `2px dashed ${DS.line}`,
                        }}>
                          <MailOutlineIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: DS.ink2, mb: "4px" }}>
                          No pending invitations
                        </Typography>
                        <Typography sx={{ fontSize: "12.5px", color: DS.ink3, maxWidth: 280, mx: "auto" }}>
                          When you send an invitation, it will appear here until the recipient accepts.
                        </Typography>
                      </Box>
                    ) : (
                      allPending.map((inv) => {
                        const expDays = inv.expires ? daysUntil(inv.expires) : null;
                        return (
                          <Box key={inv.id} sx={{
                            display: "flex", alignItems: "center", gap: "14px",
                            p: "13px 20px", borderBottom: `1px solid ${DS.line2}`,
                            "&:last-child": { borderBottom: 0 },
                          }}>
                            <Box sx={{
                              width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                              display: "grid", placeItems: "center", bgcolor: DS.surface3,
                              color: DS.ink4, border: `2px dashed ${DS.line}`,
                            }}>
                              <PersonIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {inv.email}
                              </Typography>
                              <Typography sx={{ fontSize: "12px", color: DS.ink3, mt: "1px" }}>
                                Invited {formatDate(inv.date)}
                                {expDays !== null && expDays > 0 ? ` · expires in ${expDays} day${expDays !== 1 ? "s" : ""}` : expDays !== null && expDays <= 0 ? " · expired" : ""}
                              </Typography>
                            </Box>
                            <Box sx={{
                              fontSize: "10.5px", fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
                              px: "9px", py: "3px", borderRadius: "999px",
                              bgcolor: tAccent50, color: tAccent, border: `1px solid ${tAccent100}`,
                              flexShrink: 0,
                            }}>
                              {inv.status}
                            </Box>
                            {inv.type === "email" && (
                              <>
                                <Button
                                  size="small"
                                  onClick={() => handleResend(inv.rawId)}
                                  sx={{
                                    height: 32, px: "12px", borderRadius: "8px", textTransform: "none",
                                    fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap", flexShrink: 0,
                                    border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, opacity: 0.75,
                                    "&:hover": { bgcolor: DS.surface2, opacity: 1 },
                                  }}
                                >
                                  Resend
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevoke(inv.rawId)}
                                  sx={{
                                    width: 32, height: 32, borderRadius: "8px", flexShrink: 0,
                                    border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink3,
                                    "&:hover": { bgcolor: DS.danger50, color: DS.danger, borderColor: DS.danger100 },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Paper>
              </Box>

              {/* Sidebar */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", position: "sticky", top: 24 }}>
                {/* Course Load */}
                <Paper elevation={0} sx={{ display: "none" }}>
                  <Box sx={{ p: "14px 18px 12px", borderBottom: `1px solid ${DS.line2}` }}>
                    <Typography sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: 600, color: DS.ink, fontFamily: "var(--font-display)" }}>
                      <SchoolIcon sx={{ fontSize: 15, color: DS.ink3 }} />
                      Course Load
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {classLoadItems.length === 0 ? (
                      <Box sx={{ p: "18px", textAlign: "center" }}>
                        <Typography sx={{ fontSize: "12.5px", color: DS.ink3 }}>No courses yet.</Typography>
                      </Box>
                    ) : classLoadItems.map((cls) => {
                      const total = cls.capacity || cls.maxStudents || 0;
                      const available = cls.availableSeats ?? total;
                      const filled = Math.max(0, total - available);
                      const pct = total > 0 ? (filled / total) * 100 : 0;
                      let barColor = tSecondary;
                      if (available === 0) barColor = DS.danger;
                      else if (available <= 3 || (total > 0 && available / total <= 0.25)) barColor = tAccent;
                      const instIds = cls.leadInstructorId ? [Number(cls.leadInstructorId)] : [];
                      const names = instIds
                        .map((id) => { const inst = instructors.find((i) => Number(i.id) === id); return inst ? (inst.fullName || inst.username) : null; })
                        .filter(Boolean);
                      return (
                        <Box key={cls.id} sx={{ p: "12px 18px", borderBottom: `1px solid ${DS.line2}`, "&:last-child": { borderBottom: 0 } }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "7px" }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: DS.ink }}>{cls.title}</Typography>
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: DS.ink, opacity: 0.55 }}>{filled}/{total} seats</Typography>
                          </Box>
                          <Box sx={{ height: "5px", bgcolor: DS.line2, borderRadius: "999px", overflow: "hidden" }}>
                            <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: barColor, borderRadius: "999px" }} />
                          </Box>
                          {names.length > 0 && (
                            <Box sx={{ display: "flex", alignItems: "center", mt: "8px" }}>
                              {instIds.slice(0, 3).map((id) => {
                                const inst = instructors.find((i) => Number(i.id) === id);
                                const nm = inst ? (inst.fullName || inst.username) : "?";
                                return (
                                  <Box key={id} sx={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid #fff", display: "grid", placeItems: "center", fontSize: "8px", fontWeight: 700, color: "#fff", mr: "-5px", flexShrink: 0, bgcolor: getAvatarColor(nm) }}>
                                    {getInitials(nm)}
                                  </Box>
                                );
                              })}
                              <Typography sx={{ fontSize: "11.5px", color: DS.ink, opacity: 0.55, ml: "12px" }}>{names.join(", ")}</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>

                {/* Recent Activity */}
                <Paper elevation={0} sx={{ border: `1px solid ${DS.line}`, borderRadius: "12px", overflow: "hidden" }}>
                  <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    p: "14px 18px 12px", borderBottom: `1px solid ${DS.line2}`,
                  }}>
                    <Typography sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: 600, color: DS.ink, fontFamily: "var(--font-display)" }}>
                      <AccessTimeIcon sx={{ fontSize: 15, color: DS.ink3 }} />
                      Recent Activity
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {inviteHistory.length === 0 ? (
                      <Box sx={{ p: "18px", textAlign: "center" }}>
                        <Typography sx={{ fontSize: "12.5px", color: DS.ink3 }}>No activity yet.</Typography>
                      </Box>
                    ) : inviteHistory.slice(0, 8).map((inv) => {
                      const status = (inv.status || "").toUpperCase();
                      const type = status === "ACCEPTED" ? "add" : status === "CANCELLED" ? "remove" : "invite";
                      const iconBg = type === "add" ? tSecondary50 : type === "remove" ? DS.danger50 : tAccent50;
                      const iconColor = type === "add" ? tSecondary : type === "remove" ? DS.danger : tAccent;
                      const text = status === "ACCEPTED"
                        ? "accepted invitation and joined as Instructor"
                        : status === "CANCELLED"
                        ? "invitation was revoked"
                        : "invitation sent";
                      const timeStr = inv.createdAt
                        ? new Date(inv.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                        : "—";
                      return (
                        <Box key={inv.id} sx={{
                          display: "flex", alignItems: "flex-start", gap: "11px",
                          p: "11px 18px", borderBottom: `1px solid ${DS.line2}`,
                          "&:last-child": { borderBottom: 0 },
                        }}>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: "8px", flexShrink: 0,
                            display: "grid", placeItems: "center", mt: "1px",
                            bgcolor: iconBg, color: iconColor,
                          }}>
                            {type === "add" && <AddIcon sx={{ fontSize: 14 }} />}
                            {type === "invite" && <MailOutlineIcon sx={{ fontSize: 14 }} />}
                            {type === "remove" && <DeleteIcon sx={{ fontSize: 14 }} />}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: "12.5px", color: DS.ink2, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <Box component="strong" sx={{ color: DS.ink, fontWeight: 700 }}>{inv.email}</Box>{" — "}{text}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: DS.ink4, fontWeight: 600, mt: "2px" }}>
                              {timeStr}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </>
        )}

        <InviteDialog
          open={inviteOpen}
          tenantId={selectedTenantId}
          onSuccess={load}
          onClose={() => setInviteOpen(false)}
        />

        {/* Teaching Roles Dialog */}
        <Dialog open={!!rolesDialog} onClose={() => setRolesDialog(null)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,.25)" } }}>
          <Box sx={{ p: "22px 24px 14px", borderBottom: `1px solid ${DS.line2}` }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: DS.ink, fontFamily: "var(--font-display)" }}>Teaching Roles</Typography>
            <Typography sx={{ fontSize: "13px", color: DS.ink, opacity: 0.6, mt: "3px" }}>
              {rolesDialog ? `Roles ${rolesDialog.instructor.fullName || rolesDialog.instructor.username} may claim on open session slots` : ""}
            </Typography>
          </Box>
          <DialogContent sx={{ p: "12px 24px", display: "flex", flexDirection: "column" }}>
            {TEACHING_ROLES.map((r) => (
              <FormControlLabel
                key={r.value}
                label={r.label}
                control={
                  <Checkbox
                    checked={!!rolesDialog && rolesDialog.selected.includes(r.value)}
                    onChange={(e) => setRolesDialog((d) => ({
                      ...d,
                      selected: e.target.checked ? [...d.selected, r.value] : d.selected.filter((v) => v !== r.value),
                    }))}
                  />
                }
              />
            ))}
          </DialogContent>
          <DialogActions sx={{ px: "24px", pb: "18px", pt: "14px", borderTop: `1px solid ${DS.line2}`, bgcolor: DS.surface2 }}>
            <Button onClick={() => setRolesDialog(null)} sx={{ textTransform: "none", color: DS.ink }}>Cancel</Button>
            <Button onClick={handleSaveRoles} disabled={rolesSaving}
              sx={{ height: 38, px: "16px", borderRadius: "8px", bgcolor: tPrimary, color: "#fff", fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".2px", "&:hover": { bgcolor: tPrimary700 } }}>
              {rolesSaving ? "Saving…" : "Save Roles"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Course Dialog */}
        <Dialog open={!!assignDialog} onClose={() => setAssignDialog(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,.25)" } }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px", p: "22px 24px 18px", borderBottom: `1px solid ${DS.line2}` }}>
            <Box sx={{ width: 42, height: 42, borderRadius: "8px", bgcolor: tPrimary, color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
              <SchoolIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "18px", fontWeight: 600, color: DS.ink, fontFamily: "var(--font-display)" }}>
                Assign Course
              </Typography>
              <Typography sx={{ fontSize: "13px", color: DS.ink, opacity: 0.6, mt: "3px" }}>
                {assignDialog ? `Assigning a course to ${assignDialog.instructor.fullName || assignDialog.instructor.username}` : ""}
              </Typography>
            </Box>
            <IconButton onClick={() => setAssignDialog(null)} size="small" sx={{ width: 32, height: 32, borderRadius: "6px", border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, opacity: 0.6, "&:hover": { bgcolor: DS.danger50, color: DS.danger, opacity: 1 } }}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: DS.ink, opacity: 0.55 }}>
                Course <Box component="span" sx={{ color: DS.danger }}>*</Box>
              </Typography>
              <Select
                value={assignCourseId}
                onChange={(e) => setAssignCourseId(e.target.value)}
                displayEmpty
                size="small"
                sx={{ borderRadius: "8px", fontSize: "14px", bgcolor: DS.surface2, "& fieldset": { borderColor: DS.line }, "&:hover fieldset": { borderColor: DS.ink4 }, "&.Mui-focused fieldset": { borderColor: tPrimary } }}
              >
                <MenuItem value="" disabled>Select a course…</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={String(cls.id)}>{cls.title}</MenuItem>
                ))}
              </Select>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: "24px", pb: "18px", pt: "14px", borderTop: `1px solid ${DS.line2}`, bgcolor: DS.surface2, display: "flex", justifyContent: "flex-end", gap: "9px" }}>
            <Button onClick={() => setAssignDialog(null)} sx={{ height: 38, px: "16px", borderRadius: "8px", border: `1px solid ${DS.line}`, bgcolor: DS.surface, color: DS.ink, fontSize: "13px", fontWeight: 500, textTransform: "none", "&:hover": { bgcolor: DS.surface3 } }}>
              Cancel
            </Button>
            <Button onClick={handleAssignCourse} disabled={assignLoading || !assignCourseId}
              sx={{ height: 38, px: "16px", borderRadius: "8px", border: 0, bgcolor: tPrimary, color: "#fff", fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".2px", "&:hover": { bgcolor: tPrimary700 } }}>
              {assignLoading ? "Assigning…" : "Assign Course"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
